import { assertUnreachable, isNotDefined } from '@sgme/fp';
import { isBefore, parseISO } from 'date-fns';

import { toMultipassLegId } from '@/api/option/utils.ts';
import type {
  FxOption,
  HedgeType,
  LegId,
  LegsData,
  OptionLegError,
  OptionLegProperties,
  OptionQuote,
  RfsStatus,
} from '@/store/option/state.ts';
import type { CurrencyChoice } from '@/types/common.ts';

export function isPriceable(option: FxOption): boolean {
  return option.tradeCapture.status === 'CHECKED' && option.tradeCapture.isReadyToPrice;
}

export interface OptionCurrency {
  currencyPair: string;
  currency1: string;
  currency2: string;
}

export function selectOptionCurrency(option: FxOption): OptionCurrency {
  const currencyPair = option.properties.currencyPair;
  const [currency1, currency2] = currencyPair.split('/');

  return {
    currencyPair,
    currency1: currency1 ?? '',
    currency2: currency2 ?? '',
  };
}

export function selectOptionQuoteLegs(option: FxOption, legId: LegId): LegsData | undefined {
  const mpLegId = toMultipassLegId(legId, option.type);
  return getQuote(option)?.legsData?.[mpLegId];
}

export function selectOptionLegsProperties(tile: FxOption, legId: LegId): OptionLegProperties | undefined {
  return tile?.properties.legs.find((leg) => leg.legId === legId);
}

export function selectOptionLegsValidation(tile: FxOption, legId: LegId): OptionLegError | undefined {
  if (tile.tradeCapture.status !== 'CHECKED') {
    return undefined;
  }
  return tile?.tradeCapture.validation?.legs.find((leg) => leg.legId === legId);
}

export function selectQuoteHedge(option: FxOption, legId: LegId) {
  const legsProperties = selectOptionLegsProperties(option, legId);
  const hedgeType = option.properties.hedgeType;
  const quote = getQuote(option);
  if (quote === undefined) {
    return undefined;
  }

  switch (hedgeType) {
    case 'Live':
      return undefined;
    case 'Forward':
    case 'Ndf':
      return quote.hedgesData.find((hedge) => hedge.maturityDate === legsProperties?.deliveryDate);
    case 'Spot':
      return quote.hedgesData[0];
    default:
      return assertUnreachable(hedgeType, `Unknown hedge type ${hedgeType}`);
  }
}

export function selectOptionHedge(option: FxOption, legId: LegId) {
  const legProperties = selectOptionLegsProperties(option, legId);
  if (isNotDefined(legProperties)) {
    return undefined;
  }
  const hedgeType = option.properties.hedgeType;
  switch (hedgeType) {
    case 'Live':
      return undefined;
    case 'Forward':
    case 'Ndf':
      return option.properties.hedges.find((hedge) => hedge.maturityDate === legProperties.deliveryDate);
    case 'Spot':
      return option.properties.hedges[0];
    default:
      return assertUnreachable(hedgeType, `Unknown hedge type ${hedgeType}`);
  }
}

export function selectRfsStatus(option: FxOption): RfsStatus {
  if (!areAllExpiryDatesValid(option)) {
    return 'NOT_PRICEABLE';
  }

  switch (option.properties.rfs.status) {
    case 'ERROR': {
      const isBypassable = option.properties.rfs.error.multipassErrorCode === 'RrCreditCheckFailed'; // && sl.isUserInternalSales(state);
      return isBypassable ? 'BYPASSABLE_ERROR' : 'ERROR';
    }

    case 'CANCELLED':
      return isPriceable(option) ? 'PRICEABLE' : 'NOT_PRICEABLE';

    case 'EXPIRED':
      return isPriceable(option) ? 'PRICEABLE' : 'NOT_PRICEABLE';

    case 'AWAITING':
      return option.properties.rfs.quoteType === 'Tradeable' ? 'AWAITING_EXECUTABLE' : 'AWAITING_INDICATIVE';

    case 'PRICING':
      return option.properties.rfs.quoteType === 'Tradeable' ? 'STREAMING_EXECUTABLE' : 'STREAMING_INDICATIVE';

    default:
      assertUnreachable(option.properties.rfs, 'Unhandled stream status');
  }
}

export function areAllExpiryDatesValid(option: FxOption) {
  const maxDate = option.properties.maxDate;
  if (isNotDefined(maxDate)) {
    return true;
  }

  return option.properties.legs.every((leg) => {
    const expiry = leg.expiryDate;
    return isNotDefined(expiry) || isBefore(parseISO(expiry), maxDate);
  });
}

export function getQuote(option: FxOption): OptionQuote | undefined {
  if (option.properties.rfs.status === 'PRICING' || option.properties.rfs.status === 'EXPIRED') {
    return option.properties.rfs.quote;
  }
}

export function isSolvingOption(option: FxOption): boolean {
  return option.properties.legs.some((leg) => leg.strike?.type === 'solving');
}

export function getRfsId(option: FxOption): string | undefined {
  if (
    option.properties.rfs.status === 'PRICING' ||
    option.properties.rfs.status === 'AWAITING' ||
    option.properties.rfs.status === 'ERROR'
  ) {
    return option.properties.rfs.rfsId;
  }
}

export function getQuoteType(option: FxOption): 'Indicative' | 'Tradeable' | 'Withdraw' {
  if (option.properties.rfs.status === 'PRICING' || option.properties.rfs.status === 'AWAITING') {
    return option.properties.rfs.quoteType;
  }
  return 'Indicative';
}

export function isMultiLeg(option: FxOption): boolean {
  return option.type === 'Vanilla' && option.properties.legs.length > 1;
}

type HedgeDisplayMode = 'compact' | 'strategy' | 'none';

export interface HedgeDisplayRules {
  premiumDate: HedgeDisplayMode; // premiumPaymentDate
  // 'Delta hedge' in compact view => amountInCcy1
  hedgeAmount: HedgeDisplayMode;
  hedgeDate: HedgeDisplayMode; // read only
  hedgeRate: HedgeDisplayMode; // price
}

/** Forward and Ndf enable grouping temporarily, without changing the saved user preference. */
export function selectGroupedByExpiry(option: FxOption): boolean {
  return requiresExpiryGrouping(option.properties.hedgeType) || option.properties.uiState.groupLegsByExpiry;
}

export function requiresExpiryGrouping(hedgeType: HedgeType): boolean {
  return hedgeType === 'Forward' || hedgeType === 'Ndf';
}

export function computeHedgeDisplayRules(option: FxOption): HedgeDisplayRules {
  const hedgeType = option.properties.hedgeType;
  const isStrategy = option.properties.uiState.strategyPanelMode !== 'hidden';

  switch (hedgeType) {
    case 'Live':
      return {
        premiumDate: 'compact',
        hedgeAmount: 'none',
        hedgeDate: 'none',
        hedgeRate: 'none',
      };
    case 'Spot':
      return {
        premiumDate: 'compact',
        hedgeAmount: 'compact',
        hedgeDate: 'none',
        hedgeRate: 'compact',
      };
    case 'Ndf':
    case 'Forward': {
      const legs = option.properties.legs ?? [];
      const hedges = option.properties.hedges;

      // Forward and Ndf strategies show hedge fields even with one or no API hedge.
      const displayHedgeInStrat = hedges.length > 1 || (isStrategy && legs.length > 1);

      const premiumDateTenors = legs.map((leg) => leg.premiumPaymentDateTenor);
      const displayPremiumDateInStrat = !premiumDateTenors.every((tenor) => tenor === 'SP') && isStrategy;
      return {
        premiumDate: displayPremiumDateInStrat ? 'strategy' : 'compact',
        hedgeAmount: displayHedgeInStrat ? 'strategy' : 'compact',
        hedgeDate: displayHedgeInStrat ? 'strategy' : 'compact',
        hedgeRate: displayHedgeInStrat ? 'strategy' : 'compact',
      };
    }
    default:
      assertUnreachable(hedgeType, `Unknown hedge type: ${hedgeType}`);
  }
}

export function selectOptionIsReversed(option: FxOption) {
  const quote = getQuote(option);

  const optionCurrency = selectOptionCurrency(option);
  const markupCurrencyLabel = option.properties.markupCurrency;
  const markupCurrency: CurrencyChoice = markupCurrencyLabel === optionCurrency.currency2 ? 2 : 1;
  const quoteCcy = markupCurrency === 1 ? quote?.ccy1 : quote?.ccy2;

  if (isNotDefined(quoteCcy)) {
    return false;
  }
  return quoteCcy.bidAmount * -1 > quoteCcy.askAmount;
}
